import { Timespan, TimespanPredefinedType } from '@thingslog/repositories';
import {
  startOfToday,
  endOfToday,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  subDays,
  subMonths,
  subYears,
  startOfDay,
  endOfDay,
  format,
} from 'date-fns';

export class TimespanFactory {
  private static formatDate(date: Date): string {
    return format(date, 'yyyy-MM-dd');
  }

  public static create(props: MutateProps): Timespan {
    if (props.type === 'predefined') {
      return { type: 'predefined', name: props.name };
    }
    return {
      type: 'custom',
      fromDate: this.formatDate(props.fromDate),
      toDate: this.formatDate(props.toDate),
    };
  }

  public static update(timespan: Timespan, props: Partial<MutateProps>): Timespan {
    if (timespan.type === 'predefined' && props.type === 'predefined') {
      const updated = { ...timespan };
      if (props.name) {
        updated.name = props.name;
      }
      return updated;
    } else if (timespan.type === 'custom' && props.type === 'custom') {
      const updated = { ...timespan };
      if (props.fromDate) {
        updated.fromDate = this.formatDate(props.fromDate);
      }
      if (props.toDate) {
        updated.toDate = this.formatDate(props.toDate);
      }
      return updated;
    } else {
      throw new Error('Invalid timespan type');
    }
  }

  public static resolve(timespan: Timespan): ResolvedTimespan {
    const now = new Date();
    if (timespan.type === 'custom') {
      return { fromDate: timespan.fromDate, toDate: timespan.toDate };
    }

    const todayStart = startOfToday();
    const todayEnd = endOfToday();

    switch (timespan.name) {
      case 'today': {
        return { fromDate: this.formatDate(todayStart), toDate: this.formatDate(todayEnd) };
      }
      case 'yesterday':
        const yesterdayDate = subDays(todayStart, 1);
        return {
          fromDate: this.formatDate(startOfDay(yesterdayDate)),
          toDate: this.formatDate(endOfDay(yesterdayDate)),
        };
      case 'this-week': {
        const start = startOfWeek(now, { weekStartsOn: 1 });
        const end = endOfWeek(now, { weekStartsOn: 1 });
        return { fromDate: this.formatDate(start), toDate: this.formatDate(end) };
      }
      case 'this-month': {
        const start = startOfMonth(now);
        const end = endOfMonth(now);
        return { fromDate: this.formatDate(start), toDate: this.formatDate(end) };
      }
      case 'this-year': {
        const start = startOfYear(now);
        const end = endOfYear(now);
        return { fromDate: this.formatDate(start), toDate: this.formatDate(end) };
      }
      case 'past-1d': {
        const end = todayEnd;
        const start = subDays(end, 1);
        return { fromDate: this.formatDate(start), toDate: this.formatDate(end) };
      }
      case 'past-1w': {
        const end = todayEnd;
        const start = subDays(end, 7);
        return { fromDate: this.formatDate(start), toDate: this.formatDate(end) };
      }
      case 'past-1m': {
        const end = todayEnd;
        const start = subMonths(end, 1);
        return { fromDate: this.formatDate(start), toDate: this.formatDate(end) };
      }
      case 'past-1y': {
        const end = todayEnd;
        const start = subYears(end, 1);
        return { fromDate: this.formatDate(start), toDate: this.formatDate(end) };
      }
      default:
        return { fromDate: this.formatDate(todayStart), toDate: this.formatDate(todayStart) };
    }
  }
}

interface MutatePropsBase {
  type: 'predefined' | 'custom';
}

interface MutatePropsPredefined extends MutatePropsBase {
  type: 'predefined';
  name: TimespanPredefinedType;
}

interface MutatePropsCustom extends MutatePropsBase {
  type: 'custom';
  fromDate: Date;
  toDate: Date;
}

type MutateProps = MutatePropsPredefined | MutatePropsCustom;

type ResolvedTimespan = {
  fromDate: string;
  toDate: string;
};
